import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Plus, Edit, Trash2, ChevronDown, ChevronUp, FileSpreadsheet, Send, Download, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Clients = () => {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [expandedClient, setExpandedClient] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expiringFilter, setExpiringFilter] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    eik: '',
    address: '',
    contact_person: '',
    email: '',
    phone: '',
    monthly_fee: 0,
    package: 'basic',
    status: 'active',
    contract_expiry: new Date().toISOString().split('T')[0],
    vehicle_count: 0,
    vehicles: [],
    comments: '',
    drive_link: '',
    username: '',
    password: ''
  });
  const [editingId, setEditingId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (expiringFilter) params.expiring_soon = true;
      
      const res = await axios.get(`${API}/clients`, { params });
      setClients(res.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  // Re-fetch when filters change
  useEffect(() => {
    fetchClients();
  }, [searchTerm, statusFilter, expiringFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/clients/${editingId}`, formData);
        toast.success('Клиентът бе актуализиран!');
      } else {
        await axios.post(`${API}/clients`, formData);
        toast.success('Клиентът бе добавен!');
      }
      fetchClients();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Грешка при запазване');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Сигурни ли сте?')) {
      try {
        await axios.delete(`${API}/clients/${id}`);
        toast.success('Клиентът бе изтрит!');
        fetchClients();
      } catch (error) {
        toast.error('Грешка при изтриване');
      }
    }
  };

  const handleEdit = (client) => {
    // Format contract_expiry as YYYY-MM-DD for input[type="date"]
    const formattedClient = {
      ...client,
      contract_expiry: client.contract_expiry ? 
        new Date(client.contract_expiry).toISOString().split('T')[0] : 
        new Date().toISOString().split('T')[0]
    };
    setFormData(formattedClient);
    setEditingId(client.id);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      company_name: '',
      eik: '',
      address: '',
      contact_person: '',
      email: '',
      phone: '',
      monthly_fee: 0,
      package: 'basic',
      status: 'active',
      contract_expiry: new Date().toISOString().split('T')[0],
      vehicle_count: 0,
      vehicles: [],
      comments: '',
      drive_link: '',
      username: '',
      password: ''
    });
    setEditingId(null);
  };

  const getExpiryColor = (expiryDate) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffDays = Math.floor((expiry - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) return 'expiry-red';
    if (diffDays < 90) return 'expiry-yellow';
    return 'expiry-green';
  };

  const getPackageBadgeColor = (pkg) => {
    const colors = {
      basic: 'badge-blue',
      standard: 'badge-purple',
      premium: 'badge-gold'
    };
    return colors[pkg] || 'badge-blue';
  };

  const handleSendVehicleTemplate = (client) => {
    const templateUrl = 'https://customer-assets.emergentagent.com/job_fleet-dash-10/artifacts/ua5trhuo_%D0%A8%D0%B0%D0%B1%D0%BB%D0%BE%D0%BD-%D0%B0%D0%B2%D1%82%D0%BE%D0%BC%D0%BE%D0%B1%D0%B8%D0%BB%D0%B8.xlsx';
    const subject = 'Шаблон за попълване на автомобили - FleetFlow';
    const body = `Здравейте ${client.contact_person},\n\nМоля, попълнете приложения шаблон с данните за вашите автомобили.\n\nЛинк към шаблон: ${templateUrl}\n\nС уважение,\nТим на FleetFlow`;
    
    // Copy link to clipboard
    navigator.clipboard.writeText(templateUrl);
    toast.success('Линкът към шаблона е копиран! Можете да го изпратите до клиента.');
  };

  // Export clients to CSV
  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/clients/export/csv`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clients_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Клиентите са експортирани успешно!');
    } catch (error) {
      toast.error('Грешка при експорт на клиенти');
      console.error('Export error:', error);
    }
  };

  // Import clients from CSV
  const handleImportCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      toast.error('Моля, изберете CSV файл');
      return;
    }

    try {
      // Read file as text
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const csvContent = e.target.result;
          // Convert to base64
          const base64Content = btoa(unescape(encodeURIComponent(csvContent)));
          
          const response = await axios.post(`${API}/clients/import/csv`, {
            csv_content: base64Content
          });
          
          if (response.data.success) {
            toast.success(`Импортирани ${response.data.imported} клиента!`);
            if (response.data.errors && response.data.errors.length > 0) {
              console.warn('Import errors:', response.data.errors);
              toast.warning(`Внимание: ${response.data.errors.length} грешки при импорт`);
            }
            fetchClients();
          }
        } catch (error) {
          toast.error('Грешка при импорт на клиенти');
          console.error('Import error:', error);
        }
      };
      reader.readAsText(file);
    } catch (error) {
      toast.error('Грешка при четене на файла');
      console.error('File read error:', error);
    }

    // Reset file input
    event.target.value = '';
  };

  return (
    <div className="page-container" data-testid="clients-page">
      <div className="page-header">
        <h1 className="page-title">{t('clients')}</h1>
        <div className="page-actions">
          {/* Import/Export buttons */}
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv"
            onChange={handleImportCSV}
            style={{ display: 'none' }}
          />
          <Button 
            variant="outline" 
            onClick={() => fileInputRef.current.click()}
            data-testid="import-csv-btn"
          >
            <Upload size={18} /> Импорт CSV
          </Button>
          <Button 
            variant="outline" 
            onClick={handleExportCSV}
            data-testid="export-csv-btn"
          >
            <Download size={18} /> Експорт CSV
          </Button>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm} data-testid="add-client-btn">
                <Plus size={20} /> {t('add')}
              </Button>
            </DialogTrigger>
          <DialogContent className="dialog-large">
            <DialogHeader>
              <DialogTitle>{editingId ? t('edit') : t('add')} {t('clients')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="client-form" data-testid="client-form">
              <div className="form-grid">
                <Input
                  placeholder={t('company_name')}
                  value={formData.company_name}
                  onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  required
                />
                <Input
                  placeholder={t('eik')}
                  value={formData.eik}
                  onChange={(e) => setFormData({...formData, eik: e.target.value})}
                  required
                />
                <Input
                  placeholder={t('address')}
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  required
                />
                <Input
                  placeholder={t('contact_person')}
                  value={formData.contact_person}
                  onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                  required
                />
                <Input
                  type="email"
                  placeholder={t('email')}
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
                <Input
                  placeholder={t('phone')}
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                />
                <div className="form-field">
                  <label className="field-label">Месечна такса (лв) *</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="напр. 150.00"
                    value={formData.monthly_fee}
                    onChange={(e) => setFormData({...formData, monthly_fee: parseFloat(e.target.value)})}
                    required
                  />
                </div>
                <div className="form-field">
                  <label className="field-label">Пакет *</label>
                  <Select value={formData.package} onValueChange={(val) => setFormData({...formData, package: val})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="basic">{t('basic')}</SelectItem>
                      <SelectItem value="standard">{t('standard')}</SelectItem>
                      <SelectItem value="premium">{t('premium')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="form-field">
                  <label className="field-label">Статус на клиента *</label>
                  <Select value={formData.status} onValueChange={(val) => setFormData({...formData, status: val})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Активен</SelectItem>
                      <SelectItem value="suspended">Спрян</SelectItem>
                      <SelectItem value="terminated">Прекратил</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="form-field">
                  <label className="field-label">Дата на изтичане на договор *</label>
                  <Input
                    type="date"
                    value={formData.contract_expiry}
                    onChange={(e) => setFormData({...formData, contract_expiry: e.target.value})}
                    required
                  />
                </div>
                <div className="form-field">
                  <label className="field-label">Брой автомобили *</label>
                  <Input
                    type="number"
                    placeholder="напр. 5"
                    value={formData.vehicle_count}
                    onChange={(e) => setFormData({...formData, vehicle_count: parseInt(e.target.value)})}
                    required
                  />
                </div>
                <div className="form-field">
                  <label className="field-label">Username за софтуер (достъп на клиента)</label>
                  <Input
                    placeholder="напр. vik_varna"
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                  />
                </div>
                <div className="form-field">
                  <label className="field-label">Password за софтуер (парола на клиента)</label>
                  <Input
                    type="password"
                    placeholder="напр. SecurePass123"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                  />
                </div>
                <div className="form-field col-span-2">
                  <label className="field-label">Google Drive линк (за документи)</label>
                  <Input
                    placeholder="https://drive.google.com/..."
                    value={formData.drive_link}
                    onChange={(e) => setFormData({...formData, drive_link: e.target.value})}
                  />
                </div>
              </div>
              <textarea
                placeholder="Коментари (допълнителна информация)"
                value={formData.comments}
                onChange={(e) => setFormData({...formData, comments: e.target.value})}
                className="form-textarea"
              />
              
              {/* Vehicle Management */}
              <div className="vehicle-management">
                <h4>Автомобили</h4>
                {formData.vehicles.map((vehicle, index) => (
                  <div key={index} className="vehicle-row">
                    <Input
                      placeholder="Марка (напр. Mercedes-Benz)"
                      value={vehicle.make}
                      onChange={(e) => {
                        const newVehicles = [...formData.vehicles];
                        newVehicles[index].make = e.target.value;
                        setFormData({...formData, vehicles: newVehicles});
                      }}
                    />
                    <Input
                      placeholder="Модел (напр. Sprinter)"
                      value={vehicle.model}
                      onChange={(e) => {
                        const newVehicles = [...formData.vehicles];
                        newVehicles[index].model = e.target.value;
                        setFormData({...formData, vehicles: newVehicles});
                      }}
                    />
                    <Input
                      type="number"
                      placeholder="Година"
                      value={vehicle.year}
                      onChange={(e) => {
                        const newVehicles = [...formData.vehicles];
                        newVehicles[index].year = parseInt(e.target.value);
                        setFormData({...formData, vehicles: newVehicles});
                      }}
                    />
                    <Input
                      placeholder="Рег. номер (напр. В 1234 АА)"
                      value={vehicle.registration}
                      onChange={(e) => {
                        const newVehicles = [...formData.vehicles];
                        newVehicles[index].registration = e.target.value;
                        setFormData({...formData, vehicles: newVehicles});
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const newVehicles = formData.vehicles.filter((_, i) => i !== index);
                        setFormData({...formData, vehicles: newVehicles, vehicle_count: newVehicles.length});
                      }}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const newVehicles = [...formData.vehicles, { make: '', model: '', year: new Date().getFullYear(), registration: '' }];
                    setFormData({...formData, vehicles: newVehicles, vehicle_count: newVehicles.length});
                  }}
                >
                  + Добави автомобил
                </Button>
              </div>
              
              <div className="form-actions">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  {t('cancel')}
                </Button>
                <Button type="submit" data-testid="save-client-btn">{t('save')}</Button>
              </div>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <Input
          placeholder="🔍 Търси по име на фирма или лице за контакт..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <div className="filters-row">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="filter-select">
              <SelectValue placeholder="Филтър по статус" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Всички статуси</SelectItem>
              <SelectItem value="active">Активен</SelectItem>
              <SelectItem value="suspended">Спрян</SelectItem>
              <SelectItem value="terminated">Прекратил</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant={expiringFilter ? "default" : "outline"}
            onClick={() => setExpiringFilter(!expiringFilter)}
          >
            {expiringFilter ? '✓' : ''} Изтичащи договори (30 дни)
          </Button>
        </div>
      </div>

      <div className="clients-grid">
        {clients.map(client => (
          <div 
            key={client.id} 
            className="client-card client-card-clickable" 
            data-testid={`client-card-${client.id}`}
            onClick={() => {
              setSelectedClient(client);
              setDetailDialogOpen(true);
            }}
          >
            <div className="client-card-header">
              <div>
                <h3>{client.company_name}</h3>
                <p className="client-eik">ЕИК: {client.eik}</p>
              </div>
              <div className="client-badges">
                <span className={`status-badge status-${client.status}`}>
                  {client.status === 'active' ? 'Активен' : client.status === 'suspended' ? 'Спрян' : 'Прекратил'}
                </span>
                <span className={`expiry-badge ${getExpiryColor(client.contract_expiry)}`}>
                  {new Date(client.contract_expiry).toLocaleDateString('bg-BG')}
                </span>
              </div>
            </div>

            <div className="client-card-body">
              <div className="client-info-row">
                <span>{t('contact_person')}:</span>
                <span>{client.contact_person}</span>
              </div>
              <div className="client-info-row">
                <span>{t('email')}:</span>
                <span>{client.email}</span>
              </div>
              <div className="client-info-row">
                <span>{t('phone')}:</span>
                <span>{client.phone}</span>
              </div>
              <div className="client-info-row">
                <span>{t('monthly_fee')}:</span>
                <span className="fee-highlight">{client.monthly_fee} лв</span>
              </div>
              <div className="client-info-row">
                <span>{t('vehicle_count')}:</span>
                <span>{client.vehicle_count}</span>
              </div>
            </div>

            <div className="client-card-footer">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpandedClient(expandedClient === client.id ? null : client.id)}
                data-testid={`expand-client-${client.id}`}
              >
                {expandedClient === client.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                {t('vehicles')}
              </Button>
              <div className="card-actions">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handleSendVehicleTemplate(client)}
                  data-testid={`send-template-${client.id}`}
                  title="Изпрати шаблон за автомобили"
                >
                  <FileSpreadsheet size={16} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleEdit(client)} data-testid={`edit-client-${client.id}`}>
                  <Edit size={16} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(client.id)} data-testid={`delete-client-${client.id}`}>
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>

            {expandedClient === client.id && (
              <div className="vehicles-section" data-testid={`vehicles-${client.id}`}>
                <h4>{t('vehicles')}</h4>
                {client.vehicles && client.vehicles.length > 0 ? (
                  <div className="vehicles-list">
                    {client.vehicles.map((vehicle, idx) => (
                      <div key={idx} className="vehicle-item">
                        <div><strong>{t('make')}:</strong> {vehicle.make}</div>
                        <div><strong>{t('model')}:</strong> {vehicle.model}</div>
                        <div><strong>{t('year')}:</strong> {vehicle.year}</div>
                        {vehicle.registration && <div><strong>{t('registration')}:</strong> {vehicle.registration}</div>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>Няма добавени автомобили</p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Client Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="dialog-large">
          <DialogHeader>
            <DialogTitle>Информация за клиент</DialogTitle>
          </DialogHeader>
          {selectedClient && (
            <div className="client-details">
              <div className="detail-section">
                <h4>Основна информация</h4>
                <div className="detail-row">
                  <span className="detail-label">Фирма:</span>
                  <span>{selectedClient.company_name}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">ЕИК:</span>
                  <span>{selectedClient.eik}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Адрес:</span>
                  <span>{selectedClient.address}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Лице за контакт:</span>
                  <span>{selectedClient.contact_person}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Имейл:</span>
                  <span>{selectedClient.email}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Телефон:</span>
                  <span>{selectedClient.phone}</span>
                </div>
              </div>

              <div className="detail-section">
                <h4>Договор и цени</h4>
                <div className="detail-row">
                  <span className="detail-label">Месечна такса:</span>
                  <span className="fee-highlight">{selectedClient.monthly_fee} лв</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Договор изтича:</span>
                  <span className={`expiry-badge ${getExpiryColor(selectedClient.contract_expiry)}`}>
                    {new Date(selectedClient.contract_expiry).toLocaleDateString('bg-BG')}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Брой автомобили:</span>
                  <span>{selectedClient.vehicle_count}</span>
                </div>
              </div>

              <div className="detail-section highlight-section">
                <h4>Достъп до софтуер</h4>
                <div className="detail-row">
                  <span className="detail-label">Username:</span>
                  <span className="credential-text">{selectedClient.username || 'Не е въведен'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Password:</span>
                  <span className="credential-text">{selectedClient.password || 'Не е въведена'}</span>
                </div>
              </div>

              {selectedClient.drive_link && (
                <div className="detail-section">
                  <h4>Google Drive</h4>
                  <div className="detail-row">
                    <a href={selectedClient.drive_link} target="_blank" rel="noopener noreferrer" className="drive-link">
                      Отвори папка →
                    </a>
                  </div>
                </div>
              )}

              {selectedClient.comments && (
                <div className="detail-section">
                  <h4>Коментари</h4>
                  <p>{selectedClient.comments}</p>
                </div>
              )}

              {selectedClient.vehicles && selectedClient.vehicles.length > 0 && (
                <div className="detail-section">
                  <h4>Автомобили ({selectedClient.vehicles.length})</h4>
                  <div className="vehicles-list">
                    {selectedClient.vehicles.map((vehicle, idx) => (
                      <div key={idx} className="vehicle-item">
                        <div><strong>{vehicle.make} {vehicle.model}</strong> ({vehicle.year})</div>
                        {vehicle.registration && <div>Рег. №: {vehicle.registration}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clients;
